import { Box, Button, LinearProgress, Tab, Tooltip } from "@mui/material";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfoIcon16pxSvg from "../../../assets/images/icons/InfoIcon16pxSvg";
import NoRowOverlay from "../../../assets/images/icons/NoRowOverlay";
import QuestionIcon16pxSvg from "../../../assets/images/icons/QuestionIcon16pxSvg";
import DefaultTooltip from "../../../components/DefaultTooltip";
import { ControlpanelPrimaryButton, ControlpanelSuccess3thButton } from "../../../components/StyledButton";
import { CustomTabPanel, CustomTabsV2, StyledDataGrid } from "../../../components/StyledComponents";
import CustomGridToolbarQuickFilter from "../CustomGridToolbarQuickFilter";
import HistoryIcon18pxSvg from "./../../../assets/images/icons/HistoryIcon18pxSvg";
import CustomPagination from "../CustomPagination";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import Cookies from "js-cookie";
import { format } from "date-fns";
import { Switch } from "antd";
import { generateTranslateMuiTable } from "../../../utils/Function/commonFunction";
import { getEventsRecent } from "../../../axios/Eventlog/EventLogApi";
import ArrowRepeatIcon16pxSvg from "../../../assets/images/icons/ArrowRepeatIcon16pxSvg";
import RefreshCircleIcon20pxSvgV2 from "../../../assets/images/icons/RefreshCircleIcon20pxSvgV2";
import { useLoading } from "../../../context/LoadingContext";
import { useRefresh } from "../../../context/RefreshContext";
import CustomColumnsPanel from "../../../components/CustomColumnsPanel";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from "react-router-dom";
import RecentCheckInList from "./RecentCheckInList";
import RecentCheckOutList from "./RecentCheckOutList";
import RecentRequestList from "./RecentRequestList";
import ServerSearchInput from "../../../components/ServerSearchInput";

dayjs.extend(utc);
dayjs.extend(timezone);

const GridInfoTable = ({ refreshData, groupId, dispalyWeek, selectedDate }) => {
  const { genaralSettingDateFormat, genaralSettingTimeFormat, dateTimeFormat, genaralSettingTimeZone } =
    useAuth();
  const { t, i18n } = useTranslation();
  const [rowData, setRowData] = useState([]);
  const [filteredRowData, setFilteredRowData] = useState([]);
  const [filteredRowCount, setFilteredRowCount] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [gridLoading, setGridLoading] = useState(false);
  const { profile, isAdmin, isEmployee } = useAuth();
  const currentLang = i18n.language;
  const doiTen = (s) => s.split(" ").slice(1).join(" ") + " " + s.split(" ")[0];
  const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
  const [show, hide] = useLoading();
  const { handleRefreshData } = useRefresh();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");

  const columnData = useMemo(
    () => [
      {
        field: "employeeCode",
        headerName: t("common.employee-code"),
        minWidth: 150,
        flex: 1,
      },
      {
        field: "fullName",
        headerName: t("common.fullname"),
        minWidth: 220,
        flex: 1,
      },
      {
        field: "directionAndStatus",
        headerName: t("common.content-035"),
        minWidth: 150,
        flex: 1,

        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.directionAndStatus}` : value,

        valueGetter: (value) => {
          if (!value) return "";

          if (value === "CHECK_IN") {
            return [
              "IN",
              t("common.in"),
              "vao",
              "vào",
            ].join(" ");
          }

          if (value === "CHECK_OUT") {
            return [
              "OUT",
              t("common.out"),
              "ra",
            ].join(" ");
          }

          return "";
        },

        renderCell: (params) => {
          const raw = params.row.directionAndStatus;
          const isCheckIn = raw === "CHECK_IN";
          const isCheckOut = raw === "CHECK_OUT";

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                fontWeight: "bold",
                gap: 1,
              }}
            >
              <Switch
                checked={isCheckIn}
                style={{ backgroundColor: isCheckIn ? "#16A34A" : "#2563EB" }}
              />
              <span
                style={{
                  color: isCheckIn ? "#16A34A" : "#2563EB",
                  fontWeight: 500,
                }}
              >
                {isCheckIn
                  ? t("common.in").toUpperCase()
                  : isCheckOut
                    ? t("common.out").toUpperCase()
                    : "-"}
              </span>
            </Box>
          );
        },
      },
      {
        field: "time",
        headerName: t("common.content-036"),
        minWidth: 200,
        flex: 1,

        rowSpanValueGetter: (value, row) =>
          row ? `${row.id}-${row.time}` : value,

        valueGetter: (value) => {
          if (!value) return "";

          const formatStr = genaralSettingTimeFormat || "HH:mm:ss";
          const browserTZ = dayjs.tz.guess();

          // convert UTC → timezone máy
          return dayjs.utc(value).tz(browserTZ).format(formatStr);
        },

        renderCell: (params) => {
          return params.value || "-";
        },
      },
      // {
      //   field: "timeZone",
      //   headerName: t("common.content-037"),
      //   minWidth: 150,
      //   flex: 1,
      // },
    ],
    [t, dateTimeFormat]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    employeeCode: false,
    name: true,
    directionAndStatus: true,
    time: true,
    timeZone: false,
  });

  const emailUser = profile?.email;

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    Cookies.set(`GridInfoTable_${emailUser}`, JSON.stringify(newModel), {
      expires: 90,
      path: "/",
    });
  };

  useEffect(() => {
    const saved = Cookies.get(`GridInfoTable_${emailUser}`);
    if (saved) {
      setColumnVisibilityModel(JSON.parse(saved));
    }
  }, []);

  // useEffect(() => {

  const fetchData = useCallback(async () => {
    show();
    try {
      const data = await getEventsRecent({
        langId: localStorage.getItem("i18nextLngId"),
        groupId,
      });

      const employees = data?.data?.data || [];
      setRowData(
        employees.map((item, index) => ({
          ...item,
          id: index + 1,
        }))
      );
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error(t("common.content-030"));
      }
    } finally {
      hide();
    }
  }, [groupId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshData]);

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer className="flex flex-col mb-2">

        {/* LEFT */}


        {/* RIGHT */}
        <div className="flex w-full gap-3">
          {/* ✅ đặt ở đây mới đúng */}
          <CustomGridToolbarQuickFilter fullWidth />
        </div>
      </GridToolbarContainer>
    );
  };

  const isLoaded = Boolean(profile?.userId);

  const [tabValue, setTabValue] = useState(0)

  const handleChangeTab = (event, newValue) => {
    if (!isLoaded) return;

    setTabValue(newValue);
  };

  const a11yProps = (index) => {
    return {
      id: `user-tab-${index}`,
      'aria-controls': `user-tabpanel-${index}`,
    };
  }

  return (
    <div className="card px-5 py-3">
      <div className="flex justify-between items-center w-full gap-2">
        <div className="flex gap-2 items-center min-w-0">
          <p className="text-base lg:text-xl 2xl:text-lg font-semibold">
            {t("common.recent-employee-activity")}
          </p>

          <DefaultTooltip
            readMore={false}
            title={t("tool-tip.recent-employee-activity")}
            content={
              <div className="flex flex-col gap-3">
                <p>
                  {t(
                    "tool-tip.recent-user-event-list-is-shown-ordered-with-lastest-event-first"
                  )}
                </p>
              </div>
            }
          >
            <QuestionIcon16pxSvg className="fill-[#BDBDBD] shrink-0" />
          </DefaultTooltip>
        </div>

        <Tooltip title={t("button.refresh-data")} arrow>
          <ControlpanelSuccess3thButton
            onClick={handleRefreshData}
            sx={{
              width: 32,
              height: 32,
              minWidth: 32,
              p: 0,
            }}
          >
            <RefreshCircleIcon20pxSvgV2 />
          </ControlpanelSuccess3thButton>
        </Tooltip>
      </div>
      <div data-tour="dashboard-info-table">
      <CustomTabsV2
        variant="scrollable"
        indicatorColor="none"
        value={tabValue}
        onChange={handleChangeTab}
      >
        <Tab
          label={t('attendance.CHECK_IN')}
          {...a11yProps(0)}
          disableRipple
          disabled={!isLoaded}
        />

        <Tab
          label={t('attendance.CHECK_OUT')}
          {...a11yProps(1)}
          disableRipple
          disabled={!isLoaded}
        />

        <Tab
          label={t('common.request')}
          {...a11yProps(2)}
          disableRipple
          disabled={!isLoaded}
        />
      </CustomTabsV2>
      
      <div className="mt-3 mb-3">
        <ServerSearchInput
          fullWidth
          onChange={(value) => setSearchText(value)}
        />
      </div>

      
        <CustomTabPanel value={tabValue} index={0}><RecentCheckInList searchText={searchText} selectedDate={selectedDate} groupId={groupId} refreshData={refreshData} dispalyWeek={dispalyWeek} /></CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}><RecentCheckOutList searchText={searchText} selectedDate={selectedDate} groupId={groupId} refreshData={refreshData} dispalyWeek={dispalyWeek} /></CustomTabPanel>
        <CustomTabPanel value={tabValue} index={2}><RecentRequestList searchText={searchText} selectedDate={selectedDate} groupId={groupId} refreshData={refreshData} dispalyWeek={dispalyWeek} /></CustomTabPanel>
      </div>

      <ControlpanelSuccess3thButton onClick={() => navigate('/controlpanel/event-log-management')} className='!mt-4 w-full'>
        <AddIcon />
        <p>{t('common.fix-events')}</p>
      </ControlpanelSuccess3thButton>
    </div>
  );
};

export default GridInfoTable;
